import { Alert, Box, Button, CircularProgress, Container, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InventoryIcon from '@mui/icons-material/Inventory2';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { AdminProductDTO } from '@src/common-utils/DataModels';
import { caseInsensitiveComparator, currencyFormatter } from '@src/common-utils/Util';

type ProductGridContext = {
  onEdit: (product: AdminProductDTO) => void;
  onDelete: (product: AdminProductDTO) => void;
};

const ThumbnailRenderer = ({ data }: { data: AdminProductDTO }) => {
  const image = data.images?.[0];
  return image ? (
    <Box
      component="img"
      src={image.url}
      alt={data.code}
      sx={{ height: 36, width: 48, objectFit: 'contain', display: 'block', mt: '3px' }}
    />
  ) : null;
};

const ActionsRenderer = ({ data, context }: { data: AdminProductDTO; context: ProductGridContext }) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <IconButton size="small" aria-label={`Edit product ${data.code}`} onClick={() => context.onEdit(data)}>
      <EditIcon fontSize="small" />
    </IconButton>
    <IconButton size="small" aria-label={`Delete product ${data.code}`} onClick={() => context.onDelete(data)}>
      <DeleteIcon fontSize="small" />
    </IconButton>
  </Box>
);

const colDefs: ColDef<AdminProductDTO>[] = [
  { headerName: '', field: 'images', cellRenderer: ThumbnailRenderer, width: 72, sortable: false, resizable: false },
  { field: 'code', headerName: 'Code', width: 110, sort: 'asc' },
  {
    field: 'name',
    headerName: 'Name / group',
    flex: 2,
    minWidth: 160,
    comparator: caseInsensitiveComparator,
  },
  { field: 'packing', headerName: 'Packing', flex: 1, minWidth: 120 },
  { field: 'unit', headerName: 'Unit', width: 90 },
  {
    field: 'listPrice',
    headerName: 'List price',
    width: 120,
    valueFormatter: (params: ValueFormatterParams<AdminProductDTO, number>) =>
      params.value === undefined || params.value === null ? '' : currencyFormatter.format(params.value),
  },
  {
    headerName: 'Discount',
    flex: 1,
    minWidth: 130,
    valueGetter: (params) => params.data?.discount?.description ?? '',
  },
  { headerName: '', cellRenderer: ActionsRenderer, width: 96, sortable: false, resizable: false, pinned: 'right' },
];

type ProductManagementGridProps = {
  data: AdminProductDTO[];
  isLoading: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore: () => void;
  onAdd: () => void;
  onEdit: (product: AdminProductDTO) => void;
  onDelete: (product: AdminProductDTO) => void;
};

export const ProductManagementGrid = ({
  data,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onAdd,
  onEdit,
  onDelete,
}: ProductManagementGridProps) => (
  <Container component="main" maxWidth="lg" sx={{ display: 'flex', flexDirection: 'column', mt: 3, mb: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InventoryIcon />
        <Typography component="h1" variant="h5">
          Product Management
        </Typography>
      </Box>
      <Button variant="contained" onClick={onAdd} sx={{ textTransform: 'none' }}>
        Add Product
      </Button>
    </Box>

    {isLoading && (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )}

    {!isLoading && data.length === 0 && (
      <Alert severity="info" sx={{ mt: 2 }}>
        No products found
      </Alert>
    )}

    {!isLoading && data.length > 0 && (
      <>
        <Box className="ag-theme-quartz" sx={{ width: '100%', maxHeight: '70vh', overflow: 'auto' }}>
          <AgGridReact<AdminProductDTO>
            rowData={data}
            columnDefs={colDefs}
            getRowId={(params) => params.data.code}
            rowHeight={44}
            domLayout="autoHeight"
            context={{ onEdit, onDelete } as ProductGridContext}
            gridOptions={{ suppressCellFocus: true, enableCellTextSelection: true, ensureDomOrder: true }}
          />
        </Box>
        {hasNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button onClick={onLoadMore} disabled={isFetchingNextPage} sx={{ textTransform: 'none' }}>
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        )}
      </>
    )}
  </Container>
);
